/**
 * Source Selector Panel (SPRINT-15 F007)
 *
 * Granular income/commitment inclusion controls for DSCR calculation.
 * Two tables with per-row checkboxes to include/exclude items from
 * deal serviceability calculations.
 *
 * Features:
 * - Income sources table with include/exclude checkboxes
 * - Commitments table with include/exclude checkboxes
 * - Override amount input for commitments
 * - Real-time totals recalculation on toggle
 * - Proposed facility highlighted with distinct badge
 * - Excluded items shown with strikethrough + greyed styling
 *
 * States:
 * - Loading: Skeleton placeholders
 * - Empty: No data message with explanation
 * - Active: Tables with toggleable rows and running totals
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface IncomeSource {
  id: string;
  dealId: string;
  sourceType: string;
  sourceEntityId: string | null;
  sourceLabel: string;
  amount: number;
  includeInCalculation: boolean;
  periodLabel: string | null;
  notes: string | null;
}

interface CommitmentItem {
  id: string;
  dealId: string;
  commitmentId: string | null;
  commitmentLabel: string;
  entityName: string | null;
  lenderName: string | null;
  annualAmount: number;
  overrideAmount: number | null;
  includeInCalculation: boolean;
  isProposed: boolean;
  notes: string | null;
}

export interface SourceSelectorProps {
  dealId: string;
  onTotalsChange?: (income: number, commitments: number) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/** Human-readable label for source type enums */
const SOURCE_TYPE_LABELS: Record<string, string> = {
  entity_ebitda: "Entity EBITDA",
  guarantor_income: "Guarantor Income",
  rental_income: "Rental Income",
  intercompany: "Intercompany",
  adjustment: "Adjustment",
  other: "Other",
};

function getSourceTypeLabel(sourceType: string): string {
  return SOURCE_TYPE_LABELS[sourceType] ?? sourceType;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SourceSelector: React.FC<SourceSelectorProps> = ({
  dealId,
  onTotalsChange,
}) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Inline override editing state: commitmentId -> local string value
  const [overrideEdits, setOverrideEdits] = useState<Record<string, string>>(
    {}
  );

  // -----------------------------------------------------------------------
  // Queries
  // -----------------------------------------------------------------------

  const {
    data: incomeSources = [],
    isLoading: incomeLoading,
    error: incomeError,
  } = useQuery<IncomeSource[]>({
    queryKey: [`/api/financial/deals/${dealId}/income-inclusion`],
    enabled: !!dealId,
  });

  const {
    data: commitments = [],
    isLoading: commitmentsLoading,
    error: commitmentsError,
  } = useQuery<CommitmentItem[]>({
    queryKey: [`/api/financial/deals/${dealId}/commitment-inclusion`],
    enabled: !!dealId,
  });

  // -----------------------------------------------------------------------
  // Totals (memo)
  // -----------------------------------------------------------------------

  const totalIncome = useMemo(
    () =>
      incomeSources
        .filter((s) => s.includeInCalculation)
        .reduce((sum, s) => sum + Number(s.amount), 0),
    [incomeSources]
  );

  const totalCommitments = useMemo(
    () =>
      commitments
        .filter((c) => c.includeInCalculation)
        .reduce(
          (sum, c) =>
            sum +
            (c.overrideAmount != null
              ? Number(c.overrideAmount)
              : Number(c.annualAmount)),
          0
        ),
    [commitments]
  );

  // Notify parent when totals change
  useEffect(() => {
    onTotalsChange?.(totalIncome, totalCommitments);
  }, [totalIncome, totalCommitments, onTotalsChange]);

  // -----------------------------------------------------------------------
  // Mutations — income toggle
  // -----------------------------------------------------------------------

  const toggleIncomeMutation = useMutation({
    mutationFn: async ({
      id,
      includeInCalculation,
    }: {
      id: string;
      includeInCalculation: boolean;
    }) => {
      await apiRequest(
        "PATCH",
        `/api/financial/deals/${dealId}/income-inclusion/${id}`,
        { includeInCalculation }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/financial/deals/${dealId}/income-inclusion`],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update income inclusion",
        variant: "destructive",
      });
    },
  });

  // -----------------------------------------------------------------------
  // Mutations — commitment toggle
  // -----------------------------------------------------------------------

  const toggleCommitmentMutation = useMutation({
    mutationFn: async ({
      id,
      includeInCalculation,
    }: {
      id: string;
      includeInCalculation: boolean;
    }) => {
      await apiRequest(
        "PATCH",
        `/api/financial/deals/${dealId}/commitment-inclusion/${id}`,
        { includeInCalculation }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/financial/deals/${dealId}/commitment-inclusion`],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to update commitment inclusion",
        variant: "destructive",
      });
    },
  });

  // -----------------------------------------------------------------------
  // Mutations — commitment override amount
  // -----------------------------------------------------------------------

  const overrideMutation = useMutation({
    mutationFn: async ({
      id,
      overrideAmount,
    }: {
      id: string;
      overrideAmount: number | null;
    }) => {
      await apiRequest(
        "PATCH",
        `/api/financial/deals/${dealId}/commitment-inclusion/${id}`,
        { overrideAmount }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/financial/deals/${dealId}/commitment-inclusion`],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update override amount",
        variant: "destructive",
      });
    },
  });

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleIncomeToggle = useCallback(
    (id: string, currentValue: boolean) => {
      toggleIncomeMutation.mutate({
        id,
        includeInCalculation: !currentValue,
      });
    },
    [toggleIncomeMutation]
  );

  const handleCommitmentToggle = useCallback(
    (id: string, currentValue: boolean) => {
      toggleCommitmentMutation.mutate({
        id,
        includeInCalculation: !currentValue,
      });
    },
    [toggleCommitmentMutation]
  );

  const handleOverrideBlur = useCallback(
    (id: string) => {
      const rawValue = overrideEdits[id];
      // Clear local edit state
      setOverrideEdits((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });

      if (rawValue === undefined) return;

      const trimmed = rawValue.trim();
      if (trimmed === "") {
        // Clear override
        overrideMutation.mutate({ id, overrideAmount: null });
        return;
      }

      const numValue = parseFloat(trimmed);
      if (isNaN(numValue) || numValue < 0) {
        toast({
          title: "Invalid amount",
          description: "Override amount must be a positive number",
          variant: "destructive",
        });
        return;
      }

      overrideMutation.mutate({ id, overrideAmount: numValue });
    },
    [overrideEdits, overrideMutation, toast]
  );

  // -----------------------------------------------------------------------
  // Loading state
  // -----------------------------------------------------------------------

  const isLoading = incomeLoading || commitmentsLoading;

  if (isLoading) {
    return (
      <Card data-testid="source-selector-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // -----------------------------------------------------------------------
  // Error state
  // -----------------------------------------------------------------------

  if (incomeError || commitmentsError) {
    return (
      <Card data-testid="source-selector-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">
            Income &amp; Commitment Sources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">
            Failed to load source data. Please try refreshing the page.
          </p>
        </CardContent>
      </Card>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <Card data-testid="source-selector-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-semibold">
            Income &amp; Commitment Sources
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* ─── Income Sources Table ─── */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" />
            INCOME SOURCES INCLUDED
          </h4>

          {incomeSources.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No income sources configured for this deal
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm"
                data-testid="income-sources-table"
              >
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground w-8">
                      {/* checkbox column */}
                    </th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                      Source
                    </th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">
                      Amount
                    </th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                      Entity
                    </th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                      Period
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {incomeSources.map((source) => (
                    <tr
                      key={source.id}
                      className={cn(
                        "border-b transition-opacity",
                        !source.includeInCalculation && "opacity-50"
                      )}
                    >
                      <td className="py-2 px-2">
                        <Checkbox
                          checked={source.includeInCalculation}
                          onCheckedChange={() =>
                            handleIncomeToggle(
                              source.id,
                              source.includeInCalculation
                            )
                          }
                          disabled={toggleIncomeMutation.isPending}
                          data-testid={`income-toggle-${source.id}`}
                        />
                      </td>
                      <td
                        className={cn(
                          "py-2 px-2",
                          !source.includeInCalculation && "line-through"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span>
                            {source.sourceLabel ||
                              getSourceTypeLabel(source.sourceType)}
                          </span>
                          <Badge variant="outline" className="text-[10px]">
                            {getSourceTypeLabel(source.sourceType)}
                          </Badge>
                        </div>
                      </td>
                      <td
                        className={cn(
                          "py-2 px-2 text-right font-mono",
                          !source.includeInCalculation && "line-through",
                          source.amount < 0
                            ? "text-red-600"
                            : "text-foreground"
                        )}
                      >
                        {formatCurrency(source.amount)}
                      </td>
                      <td
                        className={cn(
                          "py-2 px-2 text-muted-foreground",
                          !source.includeInCalculation && "line-through"
                        )}
                      >
                        {source.sourceEntityId ?? "—"}
                      </td>
                      <td
                        className={cn(
                          "py-2 px-2 text-muted-foreground",
                          !source.includeInCalculation && "line-through"
                        )}
                      >
                        {source.periodLabel ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold border-t">
                    <td className="py-2 px-2" colSpan={2}>
                      = TOTAL INCOME
                    </td>
                    <td
                      className="py-2 px-2 text-right font-mono"
                      data-testid="total-income-value"
                    >
                      {formatCurrency(totalIncome)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* ─── Commitments Table ─── */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
            <TrendingDown className="h-3.5 w-3.5" />
            DEBT SERVICE OBLIGATIONS
          </h4>

          {commitments.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No commitments configured for this deal
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm"
                data-testid="commitments-table"
              >
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground w-8">
                      {/* checkbox column */}
                    </th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                      Commitment
                    </th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">
                      Annual
                    </th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">
                      Override
                    </th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                      Entity
                    </th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                      Lender
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {commitments.map((commitment) => {
                    const isEditing = commitment.id in overrideEdits;
                    const displayOverride = isEditing
                      ? overrideEdits[commitment.id]
                      : commitment.overrideAmount != null
                        ? String(commitment.overrideAmount)
                        : "";

                    return (
                      <tr
                        key={commitment.id}
                        className={cn(
                          "border-b transition-opacity",
                          !commitment.includeInCalculation && "opacity-50",
                          commitment.isProposed && "bg-blue-50/50"
                        )}
                      >
                        <td className="py-2 px-2">
                          <Checkbox
                            checked={commitment.includeInCalculation}
                            onCheckedChange={() =>
                              handleCommitmentToggle(
                                commitment.id,
                                commitment.includeInCalculation
                              )
                            }
                            disabled={
                              toggleCommitmentMutation.isPending
                            }
                            data-testid={`commitment-toggle-${commitment.id}`}
                          />
                        </td>
                        <td
                          className={cn(
                            "py-2 px-2",
                            !commitment.includeInCalculation &&
                              "line-through"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <span>{commitment.commitmentLabel}</span>
                            {commitment.isProposed && (
                              <Badge
                                variant="default"
                                className="text-[10px]"
                              >
                                PROPOSED
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td
                          className={cn(
                            "py-2 px-2 text-right font-mono",
                            !commitment.includeInCalculation &&
                              "line-through"
                          )}
                        >
                          {formatCurrency(commitment.annualAmount)}
                        </td>
                        <td className="py-2 px-2 text-right">
                          <Input
                            type="number"
                            step="1000"
                            min="0"
                            value={displayOverride}
                            onChange={(e) =>
                              setOverrideEdits((prev) => ({
                                ...prev,
                                [commitment.id]: e.target.value,
                              }))
                            }
                            onBlur={() =>
                              handleOverrideBlur(commitment.id)
                            }
                            placeholder="—"
                            className="h-7 w-28 text-right text-xs font-mono"
                            data-testid={`override-input-${commitment.id}`}
                            disabled={overrideMutation.isPending}
                          />
                        </td>
                        <td
                          className={cn(
                            "py-2 px-2 text-muted-foreground",
                            !commitment.includeInCalculation &&
                              "line-through"
                          )}
                        >
                          {commitment.entityName ?? "—"}
                        </td>
                        <td
                          className={cn(
                            "py-2 px-2 text-muted-foreground",
                            !commitment.includeInCalculation &&
                              "line-through"
                          )}
                        >
                          {commitment.lenderName ?? "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="font-semibold border-t">
                    <td className="py-2 px-2" colSpan={2}>
                      = TOTAL DEBT SERVICE
                    </td>
                    <td
                      className="py-2 px-2 text-right font-mono"
                      data-testid="total-commitments-value"
                    >
                      {formatCurrency(totalCommitments)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SourceSelector;
